import { useEffect, useMemo, useRef, useState } from 'react';

export interface Series {
  label: string;
  values: Array<number | undefined>;
  color: string;
}

interface Props {
  times: string[];
  series: Series[];
  min?: number;
  max?: number;
  format: (value: number) => string;
  height?: number;
  /** ドラッグで選んだ範囲の開始と終わり（ISO）。渡したときだけドラッグで選べる */
  onSelect?: (from: string, to: string) => void;
}

const PAD = { top: 10, right: 8, bottom: 18, left: 40 };

/** 依存なしの軽量な折れ線グラフ。ホバーで値を表示する。onSelect があればドラッグで範囲を選べる */
export function LineChart({ times, series, min, max, format, height = 140, onSelect }: Props) {
  const [hover, setHover] = useState<number>();
  const [drag, setDrag] = useState<{ start: number; end: number }>();
  const [WIDTH, setWidth] = useState(600);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(200, Math.round(entry.contentRect.width)));
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;

  const { lo, hi } = useMemo(() => {
    const values = series.flatMap((s) => s.values.filter((v): v is number => v !== undefined));
    const dataMax = values.length ? Math.max(...values) : 1;
    const dataMin = values.length ? Math.min(...values) : 0;
    const top = max ?? (dataMax === 0 ? 1 : dataMax * 1.15);
    return { lo: min ?? Math.min(0, dataMin), hi: top };
  }, [series, min, max]);

  const x = (i: number) => PAD.left + (times.length <= 1 ? 0 : (i / (times.length - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - ((v - lo) / (hi - lo || 1)) * innerH;

  const paths = series.map((s) => {
    let d = '';
    let pen = false;
    s.values.forEach((v, i) => {
      if (v === undefined) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  });

  const ticks = [lo, (lo + hi) / 2, hi];
  // 表示範囲が 1 日を超えるときは日付も出す
  const spanMs = times.length > 1 ? Date.parse(times[times.length - 1]!) - Date.parse(times[0]!) : 0;
  const timeLabel = (iso: string | undefined) => {
    if (!iso) return '';
    const date = new Date(iso);
    return spanMs > 86_400_000
      ? date.toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  const indexAt = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const ratio = (px - PAD.left) / innerW;
    return Math.round(ratio * (times.length - 1));
  };
  const clamp = (index: number) => Math.min(times.length - 1, Math.max(0, index));
  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const index = indexAt(event);
    setHover(index >= 0 && index < times.length ? index : undefined);
    if (drag) setDrag({ ...drag, end: clamp(index) });
  };
  const onDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!onSelect || times.length < 2 || event.button !== 0) return;
    // ドラッグで周りの文字が選択されないようにする
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const index = clamp(indexAt(event));
    setDrag({ start: index, end: index });
  };
  const onUp = () => {
    if (!drag) return;
    setDrag(undefined);
    const a = Math.min(drag.start, drag.end);
    const b = Math.max(drag.start, drag.end);
    if (a === b) return;
    // 終わりは選んだ最後の区切りの次の区切りまで含める
    onSelect?.(times[a]!, times[b + 1] ?? times[b]!);
  };

  return (
    <div style={{ position: 'relative', userSelect: onSelect ? 'none' : undefined }} ref={boxRef}>
      <svg
        className="chart"
        viewBox={`0 0 ${WIDTH} ${height}`}
        onPointerMove={onMove}
        onPointerDown={onDown}
        onPointerUp={onUp}
        onPointerCancel={() => setDrag(undefined)}
        onPointerLeave={() => setHover(undefined)}
        style={{ height, touchAction: onSelect ? 'pan-y' : undefined, cursor: onSelect ? 'crosshair' : undefined }}
        role="img"
        aria-label={series.map((s) => s.label).join('、')}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              className="chart-axis"
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(t)}
              y2={y(t)}
              strokeDasharray="2 4"
            />
            <text className="chart-label" x={PAD.left - 6} y={y(t) + 3} textAnchor="end">
              {format(t)}
            </text>
          </g>
        ))}
        <text className="chart-label" x={PAD.left} y={height - 4}>
          {timeLabel(times[0])}
        </text>
        <text className="chart-label" x={WIDTH - PAD.right} y={height - 4} textAnchor="end">
          {timeLabel(times[times.length - 1])}
        </text>
        {paths.map((d, i) => (
          <path
            key={series[i]?.label}
            d={d}
            fill="none"
            stroke={series[i]?.color}
            strokeWidth={1.6}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
          />
        ))}
        {drag && drag.start !== drag.end && (
          <rect
            className="chart-selection"
            x={x(Math.min(drag.start, drag.end))}
            width={Math.abs(x(drag.end) - x(drag.start))}
            y={PAD.top}
            height={innerH}
          />
        )}
        {hover !== undefined && (
          <line className="chart-axis" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH} />
        )}
      </svg>
      {hover !== undefined && (
        <div className="small muted" style={{ position: 'absolute', top: 0, right: 8 }}>
          {timeLabel(times[hover])}{' '}
          {series.map((s) => {
            const v = s.values[hover];
            return (
              <span key={s.label} style={{ color: s.color, marginLeft: 6 }}>
                {s.label} {v === undefined ? '—' : format(v)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
